import { useState } from "react";
import { CalendarDays, Droplet, Ellipsis, HeartPulse, House, ListTodo, SquareCheck, Wallet } from "lucide-react";
import { anchorOf, useUi, type Anchor, type Page } from "../state/ui";
import { SECTIONS, isSectionActive, useSectionCounts } from "./AppNav";
import { Menu } from "./ui/Menu";

const ICONS: Record<string, typeof House> = {
  home: House,
  calendar: CalendarDays,
  tasks: SquareCheck,
  resolutions: ListTodo,
  health: HeartPulse,
  finance: Wallet,
  cycle: Droplet
};

/** Tab bars hold five items: the first four sections, then "Mais" with the rest. */
const PRIMARY = 4;

/** Bottom section bar on phones (the top nav's links are hidden there). Styled in AppNav.css. */
export function NavTabs() {
  const ui = useUi();
  const counts = useSectionCounts();
  const [more, setMore] = useState<Anchor | null>(null);
  const extra = SECTIONS.slice(PRIMARY);
  // "Mais" takes the look of the section it's showing.
  const current = extra.find(({ page }) => isSectionActive(ui.page, page));
  const MoreIcon = current ? ICONS[current.page] : Ellipsis;
  const moreCount = extra.reduce((n, { page }) => n + (counts[page] ?? 0), 0);

  return (
    <nav className="nav-tabs" aria-label="Seções">
      {SECTIONS.slice(0, PRIMARY).map(({ page, label }) => {
        const Icon = ICONS[page];
        const active = isSectionActive(ui.page, page as Page);
        return (
          <button key={page} className={`nav-tab${active ? " active" : ""}`} aria-current={active ? "page" : undefined} onClick={() => ui.setPage(page)}>
            <Icon size={20} strokeWidth={1.75} />
            <span>{label}</span>
            {(counts[page] ?? 0) > 0 && <span className="nav-tab-count">{counts[page]}</span>}
          </button>
        );
      })}
      <button
        className={`nav-tab${current ? " active" : ""}`}
        aria-current={current ? "page" : undefined}
        aria-haspopup="menu"
        onClick={(e) => setMore(anchorOf(e.currentTarget))}
      >
        <MoreIcon size={20} strokeWidth={1.75} />
        <span>{current?.label ?? "Mais"}</span>
        {moreCount > 0 && <span className="nav-tab-count">{moreCount}</span>}
      </button>
      {more && (
        <Menu
          anchor={more}
          onClose={() => setMore(null)}
          items={extra.map(({ page, label }) => {
            const Icon = ICONS[page];
            return {
              key: page,
              label: counts[page] ? `${label} · ${counts[page]}` : label,
              icon: <Icon size={16} />,
              active: isSectionActive(ui.page, page),
              onSelect: () => ui.setPage(page)
            };
          })}
        />
      )}
    </nav>
  );
}
